import { useMemo, useRef } from "react"

import { curriculumAtoms } from "#atoms"
import GenericFormModal from "#blocks/Form/GenericFormModal"
import Firebase, { NULL_ID } from "#classes/Firebase"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  type AddCurriculumFormSubmitValues,
  type AddCurriculumFormValues,
  CurriculumConstructor,
} from "#customTypes/composedTypes/curriculumTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import { useFirebaseTransaction } from "#hooks/reactQuery/useFirebaseMutation"
import useAnalytics from "#hooks/useAnalytics"
import { getCurrentSemester } from "#utils/semesterHelpers"

import AddCurriculumForm from "./AddCurriculumForm"
import { AddCurriculumFormProvider } from "./AddCurriculumFormContext"

const defaultCurriculumValues = (): AddCurriculumFormValues => ({
  id: undefined,
  name: "",
  semester: getCurrentSemester(),
  numberOfSessions: 0,
  group: undefined,
  cycle: "",
  skillLevels: [],
  notes: "",
  projectIds: [],
})

export interface AddCurriculumFormModalProps {
  onClose: () => void
  curriculumId?: Uuid | null
}

const AddCurriculumFormModal = ({
  onClose,
  curriculumId,
}: AddCurriculumFormModalProps) => {
  const isEdit = curriculumId != null
  const { trackCurriculumAction } = useAnalytics()
  const projectIdsRef = useRef<Uuid[]>([])

  const { canCreate, canRead, canUpdate, canDelete } = useResourcePermissions(
    RESOURCES.CURRICULUM_PLAN
  )
  const curriculum = useOneDoc<CurriculumConstructor>(
    curriculumAtoms,
    DOC_TYPES.CURRICULUMS,
    (canRead && curriculumId) || NULL_ID
  )

  const initialValues = useMemo(() => {
    if (!curriculumId || !curriculum || !canRead) {
      return defaultCurriculumValues()
    }

    const curriculumFormData: AddCurriculumFormValues = {
      ...curriculum,
      numberOfSessions: curriculum.numberOfSessions,
      notes: curriculum.notes ?? "",
      skillLevels: curriculum.skillLevels ?? [],
      group: curriculum.group ?? "",
      cycle: curriculum.cycle ?? "",
    }

    return curriculumFormData
  }, [curriculum, curriculumId, canRead])

  // Errors are handled in GenericFormModal
  const handleFinish = async (
    cleanedValues: AddCurriculumFormSubmitValues
  ): Promise<void> => {
    if (!canCreate && !isEdit) {
      throw new Error("You do not have permission to create curriculums")
    }
    if (!canUpdate && isEdit) {
      throw new Error("You do not have permission to update curriculums")
    }

    // Merge projectIds from context state (source of truth) into form values
    const valuesWithProjects: AddCurriculumFormSubmitValues = {
      ...cleanedValues,
      projectIds: projectIdsRef.current,
    }

    // TODO: Add checks for classes using this curriculum
    const curriculumId = await Firebase.getInstance().createOrUpdateCurriculum(
      valuesWithProjects,
      initialValues as AddCurriculumFormSubmitValues
    )

    trackCurriculumAction(
      isEdit
        ? ANALYTICS_EVENTS.CURRICULUM_UPDATED
        : ANALYTICS_EVENTS.CURRICULUM_CREATED,
      {
        curriculumId: curriculumId!,
        curriculumName: cleanedValues.name,
      }
    )
  }

  const { mutate: handleTransaction } = useFirebaseTransaction()

  const handleDelete = async () => {
    if (!initialValues?.id) return

    if (!canDelete) {
      throw new Error("You do not have permission to delete curriculums")
    }

    // Check if any classes are using this curriculum
    const classes =
      await Firebase.getInstance().variableQuery<ClassConstructor>(
        DOC_TYPES.CLASSES,
        {
          queries: {
            curriculumId: {
              value: initialValues.id,
              comparator: COMPARATORS.EQUALS,
            },
          },
        }
      )

    if (classes.length > 0) {
      const message = `Please remove this curriculum from the following classes before deleting it:

${classes.map((c) => c.name)?.join("\n")}`
      throw new Error(message)
    }

    await handleTransaction([
      {
        docType: DOC_TYPES.CURRICULUMS,
        id: initialValues.id,
        data: null,
        action: "delete",
      },
    ])

    trackCurriculumAction(ANALYTICS_EVENTS.CURRICULUM_DELETED, {
      curriculumId: initialValues.id,
      curriculumName: initialValues.name!,
    })

    // Brief delay to allow Firebase listeners to process the deletion
    setTimeout(() => {
      onClose()
    }, 500)
  }

  return (
    <GenericFormModal<AddCurriculumFormValues, AddCurriculumFormSubmitValues>
      title={
        isEdit && initialValues?.name
          ? `Curriculum: ${initialValues.name}`
          : "Add Curriculum"
      }
      onClose={onClose}
      initialValues={initialValues}
      onFinish={handleFinish}
      resource={RESOURCES.CURRICULUM_PLAN}
      onDelete={handleDelete}
    >
      <AddCurriculumFormProvider
        initialProjectIds={initialValues.projectIds ?? []}
        projectIdsRef={projectIdsRef}
      >
        <AddCurriculumForm initialValues={initialValues} />
      </AddCurriculumFormProvider>
    </GenericFormModal>
  )
}

export default AddCurriculumFormModal
